
import {Component, Vue, Watch} from "vue-property-decorator";
import vBreadcrumbs from "@/components/vBreadcrumbs.vue";
import vTabbedContent from "@/components/vTabbedContent.vue";
import UserRepository from "@/utils/API/UserRepository";
import { mapState } from "vuex";
import { TRANSLATIONS_FETCH } from "@/store/ActionTypes";
import CustomersRepository from "@/utils/API/CustomersRepository";
import { NutrientSource } from "@/models/NutrientSource/NutrientSource";
import SubscriptionRepository from "@/utils/API/SubscriptionRepository";
import vModal from "@/components/vModal.vue";
import vTable from "@/components/vTable.vue";
import ListRequest, { Ordering, Filter, OrderType } from "@/models/ListRequest";
import Language from "@/models/Language";
import { Recipe } from "@/models/Recipes/Recipe";
import RecipesRepository from "@/utils/API/RecipesRepository";
import { RecipeCategory } from "@/models/Recipes/RecipeCategory";
import { UpdateRecipeCategory } from "@/models/Recipes/UpdateRecipeCategory";
import { DeleteRecipeCategory } from "@/models/Recipes/DeleteRecipeCategory";
import { MoveRecipesToCategory } from "@/models/Recipes/MoveRecipesToCategory";
import Package from "../../models/Subscriptions/SubscriptionPackages/Package";
import { dateDisplayFormat } from "@/utils/Defaults";
import { UserTypes } from "@/models/User/UserTypes";
import {desanitizeString, mapNutritions, toBase64} from "@/utils/Helpers";
import RestaurantUsersManagement from "./Partials/_RestaurantUsersManagement.vue";
import { AddOnTypeName } from "@/models/Subscriptions/AddOns/AddOnTypeName";
import { SubscriptionStatus } from "@/models/Subscriptions/SubscriptionStatus";
import { PaymentProvider } from "@/models/Subscriptions/PaymentProvider";
import {ITab} from "@/components/proptypes/ITab";
import VDragAndDropUpload from "@/components/vDragAndDropUpload.vue";
import restaurantRepository from "@/utils/API/RestaurantRepository";

interface AccountState {
  languages: Language[];
  language: string;
  nutrientSources: NutrientSource[];
  nutrient: any;
}
interface RecipesState {
  count: number;
  totalCount: number;
  page: number;
  pageSize: number;
  orders: Ordering;
  filters: Filter[];
  loading: boolean;

  recipes: Recipe[];
  recipe: Recipe;

  categories: RecipeCategory[];
  category: RecipeCategory | null;
  moveCategory: RecipeCategory | null;

  selectCategories: RecipeCategory[];
}

@Component({
  components: {
    VDragAndDropUpload,
    vBreadcrumbs,
    vTabbedContent,
    vTable,
    vModal,
    RestaurantUsersManagement,
  },
  computed: {
    ...mapState({
      localizationState: (state: any) => state.LocalizationModule,
      accountStoreState: (state: any) => state.AccountModule,
    }),
  },
})
class AccountSettings extends Vue {
  localizationState: any;
  accountStoreState: any;

  // make accessible in the template
  UserTypes = UserTypes;

  brandingFiles: File[] = [];

  accountState: AccountState = {
    languages: [],
    language: "",
    nutrientSources: [],
    nutrient: -1,
  };

  transformRecipes: any = {};
  nutrientSelected: number = -1;

  recipesState: RecipesState = {
    count: 0,
    totalCount: 0,
    page: 1,
    pageSize: 5,
    orders: { field: "Name", orderType: OrderType.Asc },
    filters: [],
    loading: true,

    recipes: [],
    recipe: new Recipe(),
    categories: [],
    category: { id: 0, name: "" },
    moveCategory: { id: 0, name: "" },

    selectCategories: [],
  };
  nutrientSourceEnum = NutrientSource;

  updateCategory: UpdateRecipeCategory = {
    CategoryName: "",
    CategoryId: 0,
  };
  moveRecToCat: MoveRecipesToCategory = {
    RecipesIds: [],
    RecipeCategoryId: 0,
  };
  removeCategory: DeleteRecipeCategory = {
    id: 0,
  };
  categoryTitle = "";
  selectedList: number[] = [];
  showRecipes = false;
  showDeleteCategoryModal = false;

  packages: Package[] = [];
  dateDisplayFormat = dateDisplayFormat;
  paymentProviderEnum = PaymentProvider;

  get subscriptionStatus() {
    switch (this.accountStoreState.userDetails.subscriptionStatus) {
    case 0:
      return this.translations.get("ms_draft");
    case 1:
      return this.translations.get("ms_active");
    case 2:
      return this.translations.get("ms_expired");
    case 3:
      return this.translations.get("ms_cancelled");
    default:
      return "";
    }
  }

  get shouldShowManageSubscription() {
    return (
      !this.accountStoreState.userDetails.isSpecial ||
      this.accountStoreState.userDetails.subscriptionStatus ==
      SubscriptionStatus.Expired
    );
  }

  get isOwner() {
    return this.$store.getters.getUserDetails.userType === UserTypes.Owner;
  }

  get userPackage() {
    let userPackage = this.packages.find(
      (p) => p.id === this.accountStoreState.userDetails.subscriptionPackageId
    );

    if (userPackage) {
      return userPackage;
    }
    else return {};
  }

  get freeUserLicences() {
    return this.accountStoreState.userDetails.freeUserLicenses != null
      ? this.accountStoreState.userDetails.freeUserLicenses
      : 0;
  }

  get paidUserLicences() {
    return this.accountStoreState.userDetails.paidUserLicenses != null
      ? this.accountStoreState.userDetails.paidUserLicenses
      : 0;
  }

  get tabs() {
    let tabsArray: ITab[] = [
      { id: 1, name: this.translations.get("profile_tab_categories") },
      { id: 2, name: this.translations.get("profile_tab_database_set") },
      { id: 3, name: this.translations.get("profile_tab_subscription"), predicate: () => this.shouldShowManageSubscription },
      { id: 4, name: "Branding", predicate: () => this.isOwner }
      // show the tab only to owner
      // this.$store.getters.getUserDetails.userType === UserTypes.Owner
      //   ? { id: 5, name: this.translations.get("profile_tab_restaurant_users") }
      //   : null
    ];

    return tabsArray;
  }

  get translations() {
    return this.localizationState.translations;
  }

  getAddOnName(addOnType: number) {
    switch (addOnType) {
    case 0:
      return AddOnTypeName[0];
    case 1:
      return AddOnTypeName[1];
    case 2:
      return AddOnTypeName[2];
    case 3:
      return AddOnTypeName[3];
      case 4:
        return AddOnTypeName[4];
      case 5:
        return AddOnTypeName[5];
      case 6:
        return AddOnTypeName[6];
      default:
        return "";
      }
    }

  async handleBrandingFileUpload(files: File[]) {
    const file = files[0];
    const fileAsBase64 = await toBase64(file);
    let notifTranslationKey;
    try {
      await restaurantRepository.updateRestaurantBrandingLogo(fileAsBase64);
      notifTranslationKey = "ms_save_success";
    }
    catch {
      notifTranslationKey = "ms_save_file_fail";
    }

    this.$snotify.success(this.translations.get(notifTranslationKey));
  }

  created() {
    this.$store
      .dispatch(TRANSLATIONS_FETCH, {
        category: "pages",
        collections: [
          "common",
          "profile",
          "customers",
          "recipes",
          "ingredients",
          "managesubscription",
        ],
      })
      .then(() => {
        document.title =
          this.translations.get("account") +
          " " +
          this.translations.get("meta_title_postfix");
      })
      .catch((error) => {
        if (error.response) {
          this.$snotify.error(
            error.response.data.errorMessages[0].userVisibleMessage
          );
        }
        else this.$snotify.error(this.translations.get("network-error"));
      });

    SubscriptionRepository.displaySubscriptionPackages()
      .then((response) => {
        this.packages = response.data.result;
      })
      .catch((error) => {
        if (error.response) {
          this.$snotify.error(
            error.response.data.errorMessages[0].userVisibleMessage
          );
        }
        else this.$snotify.error(this.translations.get("network-error"));
      });

    UserRepository.getUserProfile()
      .then((response) => {
        this.accountState.language = response.data.result.localization;
      })
      .catch((error) => {
        if (error.response) {
          this.$snotify.error(
            error.response.data.errorMessages[0].userVisibleMessage
          );
        }
        else this.$snotify.error(this.translations.get("network-error"));
      });

    CustomersRepository.getRestaurantProfile()
      .then((response) => {
        this.accountState.nutrient = response.data.result.defaultNutrientSource;
      })
      .catch((error) => {
        if (error.response) {
          this.$snotify.error(
            error.response.data.errorMessages[0].userVisibleMessage
          );
        }
        else this.$snotify.error(this.translations.get("network-error"));
      });

    SubscriptionRepository.listCurrentSubscriptionNutrientSources()
      .then((response) => {
        this.accountState.nutrientSources = [];
        if (
          this.accountStoreState.userDetails.availableNutrientSources.length == 1
          && this.accountStoreState.userDetails.availableNutrientSources[0] == 1
        ) {
          this.accountState.nutrientSources = [1, 3, 4];
        }
        else {
          response.data.result.map((x) => {
            if (
              x != this.nutrientSourceEnum.Branded &&
              x != this.nutrientSourceEnum.Foundation
            ) {
              this.accountState.nutrientSources.push(x);
            }
          });
        }
      })
      .catch((error) => {
        if (error.response) {
          this.$snotify.error(
            error.response.data.errorMessages[0].userVisibleMessage
          );
        }
        else this.$snotify.error(this.translations.get("network-error"));
      });

    this.fetchCategories();
  }

  onManagePaymentMethods() {
    if (
      this.accountStoreState.userDetails.featureFlagPaymentProvider ==
      this.paymentProviderEnum.Braintree
    ) {
      this.$router.push({ name: "payment" });
    }
    else if (
      this.accountStoreState.userDetails.subscriptionPaymentProvider ==
      this.paymentProviderEnum.FastSpring
    ) {
      this.getFastSpringAccountMenagementUrl();
    }
  }

  getFastSpringAccountMenagementUrl() {
    SubscriptionRepository.getFastSpringAccountMenagementUrl().then(
      (response) => {
        window.open(response.data.result);
      }
    );
  }

  resetState() {
    this.updateCategory = {
      CategoryName: "",
      CategoryId: 0,
    };
    this.recipesState.category = {
      id: 0,
      name: "",
    };
    this.moveRecToCat = {
      RecipesIds: [],
      RecipeCategoryId: 0,
    };
    this.removeCategory = {
      id: 0,
    };
    this.showRecipes = false;
    this.showDeleteCategoryModal = false;
    (<any>$("#categories")).html("").select2();
  }
  fetchCategories() {
    RecipesRepository.listRecipeCategories().then((response) => {
      this.recipesState.selectCategories = response.data.result;
      this.recipesState.categories = [];
      this.recipesState.categories.length = 0;
      let data: any[] = [];
      data.push({ id: 0, text: "" });
      response.data.result.map((r: any) => {
        data.push({ id: r.id, text: desanitizeString(r.name) });
      });
      (<any>$("#categories"))
        .select2({
          data: data,
          allowClear: false,
        })
        .on("select2:select", (eventData: any) => {
          let categoryId = eventData.target.value;
          let categoryName = $("#categories")
            .children("option:selected")
            .text();
          categoryId = parseInt(categoryId);
          this.getRecipes(categoryId, categoryName);
        });
    });
  }
  requestRecipes(listRequest: ListRequest) {
    this.recipesState.loading = true;
    RecipesRepository.listRecipes(listRequest).then((response) => {
      this.recipesState.recipes = response.data.result;
      this.recipesState.recipes.map((recipe: any) => {
        recipe.nutritionAnalysis = mapNutritions(recipe.nutritionAnalysis);
      });
      this.recipesState.count = response.data.count;
      this.recipesState.totalCount = response.data.totalCount;
      this.recipesState.loading = false;
      this.transformRecipes = this.recipesState.recipes.map(
        (element: Recipe) => {
          return {
            meta: {
              id: element.id,
              link: element.name,
            },
          };
        }
      );
    });
  }
  getRecipes(catId: number, catName: string) {
    this.recipesState.category ? (this.recipesState.category.id = catId) : 0;
    this.removeCategory.id = catId;
    this.updateCategory.CategoryId = catId;
    this.updateCategory.CategoryName = catName;
    let categoryId = catId;
    this.recipesState.moveCategory = null;
    this.categoryTitle = catName;
    if (categoryId !== 0) {
      this.showRecipes = true;
      this.requestRecipes(
        new ListRequest({
          page: 1,
          pageSize: this.recipesState.pageSize,
          filters: [{ name: "RecipeCategoryId", value: categoryId + "" }],
        })
      );
      this.recipesState.categories = [...this.recipesState.selectCategories];
      for (let i = 0; i < this.recipesState.categories.length; i++) {
        if (this.recipesState.categories[i].id === categoryId) {
          this.recipesState.categories.splice(i, 1);
        }
      }
    }
  }

  get tableHeaders() {
    return [
      {
        name: this.translations.get("ingr_table_recipe"),
        key: "Name",
      },
    ];
  }
  deleteCategory() {
    let id = this.removeCategory.id;
    // @ts-ignore
    this.$snotify.async(
      this.translations.get("rec_category_removing"),
      () =>
        new Promise((resolve, reject) => {
          RecipesRepository.deleteRecipeCategory({
            id: id,
          })
            .then(() => {
              this.resetState();
              this.fetchCategories();
              resolve({
                body: this.translations.get("rec_category_removed"),
              });
            })
            .catch((error) => {
              let message = "";
              if (error.response) {
                message =
                  error.response.data.errorMessages[0].userVisibleMessage;
              }
              else {
                message = this.translations.get("network_error");
              }
              reject({ body: message });
            });
        }),
      {
        timeout: 3000,
        closeOnClick: true,
      }
    );
  }
  moveRecipes(ids: number[], category: any) {
    this.moveRecToCat.RecipesIds = ids;
    this.moveRecToCat.RecipeCategoryId = category.id;
    let recIds = this.moveRecToCat.RecipesIds;
    // @ts-ignore
    this.$snotify.async(
      this.translations.get("rec_category_moving"),
      () =>
        new Promise((resolve, reject) => {
          RecipesRepository.moveRecipesToCategory({
            RecipesIds: this.moveRecToCat.RecipesIds,
            RecipeCategoryId: this.moveRecToCat.RecipeCategoryId,
          })
            .then(() => {
              let recipes = this.transformRecipes;
              for (let i = 0; i < recipes.length; i++) {
                for (let j = 0; j < recIds.length; j++) {
                  if (recipes[i].meta.id === recIds[j]) {
                    recipes.splice(i, 1);
                    i--;
                    break;
                  }
                }
              }
              this.changePage(1);
              resolve({
                body: this.translations.get("rec_category_moved"),
              });
            })
            .catch((error) => {
              let message = "";
              if (error.response) {
                message =
                  error.response.data.errorMessages[0].userVisibleMessage;
              }
              else {
                message = this.translations.get("network_error");
              }
              reject({ body: message });
            });
        }),
      {
        timeout: 1000,
        closeOnClick: true,
      }
    );
  }
  editCategory() {
    if (this.categoryTitle !== this.updateCategory.CategoryName) {
      // @ts-ignore
      this.$snotify.async(
        this.translations.get("rec_category_editing"),
        () =>
          new Promise((resolve, reject) => {
            RecipesRepository.renameCategory({
              CategoryName: this.updateCategory.CategoryName,
              CategoryId: this.updateCategory.CategoryId,
            })
              .then(() => {
                this.recipesState.category
                  ? (this.recipesState.category.name =
                    this.updateCategory.CategoryName)
                  : null;
                this.categoryTitle = this.recipesState.category
                  ? this.recipesState.category.name
                  : "";
                (<any>$("#categories")).select2("destroy");
                (<any>(
                  $(
                    '#categories option[value="' +
                      this.updateCategory.CategoryId +
                      '"]'
                  )
                )).text(this.updateCategory.CategoryName);
                (<any>$("#categories")).select2();

                this.fetchCategories();

                resolve({
                  body: this.translations.get("rec_category_edited"),
                });
              })
              .catch((error) => {
                let message = "";
                if (error.response) {
                  message =
                    error.response.data.errorMessages[0].userVisibleMessage;
                }
                else {
                  message = this.translations.get("network_error");
                }
                reject({ body: message });
              });
          }),
        {
          timeout: 3000,
          closeOnClick: true,
        }
      );
    }
  }
  changePage(args: any) {
    const orderings: Ordering[] = [];
    orderings.push({ ...args[3] });

    this.requestRecipes(
      new ListRequest({
        page: args[0],
        pageSize: this.recipesState.pageSize,
        filters: [
          {
            name: "RecipeCategoryId",
            value: this.recipesState.category
              ? this.recipesState.category.id + ""
              : "",
          },
        ],
        orders: orderings,
      })
    );
    this.recipesState.page = args[0];
    this.recipesState.orders = { ...args[3] };
  }
  updateDatabase(nutrient: any) {
    // @ts-ignore
    this.$snotify.async(
      this.translations.get("prof_update_database"),
      () =>
        new Promise((resolve, reject) => {
          CustomersRepository.updateDatabase({
            NutrientSource: nutrient,
          })
            .then(() => {
              resolve({
                body: this.translations.get("profile_toastr_database_updated"),
              });
            })
            .catch((error) => {
              let message = "";
              if (error.response) {
                message =
                  error.response.data.errorMessages[0].userVisibleMessage;
              }
              else {
                message = this.translations.get("network_error");
              }
              reject({ body: message });
            });
        }),
      {
        timeout: 3500,
        closeOnClick: true,
      }
    );
  }
  selectAll(selectedAll: boolean) {
    if (selectedAll === true) {
      let tmp: number[] = [];
      this.recipesState.recipes.map((recipe) => {
        tmp.push(recipe.id);
      });
      this.selectedList = tmp;
    }
    else {
      this.selectedList.length = 0;
      this.selectedList = [];
    }
  }
}
export default AccountSettings;
